import { create } from "zustand";
import type { AuthUser } from "@/marketing/models/auth";
import { isAllowedSignInEmail } from "@/config/signInAllowlist";

/** The admin AuthProvider owns the session inside the dashboard; this is its localStorage slot. */
const STORAGE_KEY = "user";

interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  /** Pushes the admin session into this store; called by MarketingAuthBridge. */
  syncFromAdmin: (user: AuthUser | null) => void;
  login: (user: AuthUser) => boolean;
  logout: () => void;
  validateDomain: (email: string) => boolean;
}

function loadUserFromStorage(): AuthUser | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;
    return JSON.parse(stored) as AuthUser;
  } catch {
    return null;
  }
}

const initialUser = loadUserFromStorage();

export const useAuthStore = create<AuthState>()((set, get) => ({
  user: initialUser,
  isAuthenticated: initialUser !== null,

  validateDomain: (email: string) => isAllowedSignInEmail(email),

  syncFromAdmin: (user: AuthUser | null) =>
    set({ user, isAuthenticated: user !== null }),

  login: (user: AuthUser) => {
    if (!get().validateDomain(user.email)) return false;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(user));
    set({ user, isAuthenticated: true });
    return true;
  },

  logout: () => {
    localStorage.removeItem(STORAGE_KEY);
    set({ user: null, isAuthenticated: false });
  },
}));
